import { useAppDispatch, useAppSelector } from 'app/hooks';
import { fetchPtacSystems } from 'features/ptac-selection';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import { useEffect, useMemo } from 'react';
import { selectActivePtacConfiguration } from 'utils/active-ptac-configuration';
import { isPtacDesignReady } from 'utils/configuration-eligibility';
import { isPtacExistingSystem } from 'utils/equipment-preferences-complete';
import { ptacElectricalDefaultsOf } from 'utils/ptac-electrical-filter';
import {
  mergePtacCustomSelections,
  ptacCustomSelectionsFromCompared
} from 'utils/ptac-custom-selections';

// Fetch orchestration for the PTAC systems on Choose Systems: one request
// per complete design, re-issued when the design, the shared gallery
// filters (in stock, price), the PTAC filters or the user's custom size
// selections change (the thunk dedupes identical bodies), and retried after
// the structure save lands.
export default function usePtacSystemsFetch(): void {
  const dispatch = useAppDispatch();
  const isPtac = useIsPtacPathway();
  const configuration = useAppSelector(selectActivePtacConfiguration);
  const anchorSystemUniqueId = useAppSelector(
    (state) =>
      state.structure.existingSystems.find(isPtacExistingSystem)?.uniqueId ??
      null
  );
  const ptacPreferences = useAppSelector((state) =>
    anchorSystemUniqueId
      ? state.structure.recommendationSelectionsBySystem[anchorSystemUniqueId]
          ?.ptac ?? null
      : null
  );
  const electricalDefaults = useMemo(
    () => ptacElectricalDefaultsOf(ptacPreferences),
    [ptacPreferences]
  );
  const galleryFilters = useAppSelector((state) => state.ui.galleryFilters);
  const zipCode = useAppSelector(
    (state) => state.project.locationData?.zipcode?.trim() || null
  );
  const filters = useAppSelector((state) => state.ptacSelection.filters);
  const sessionCustomSelections = useAppSelector(
    (state) => state.ptacSelection.customSelections
  );
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const customSystemsRevision = useAppSelector((state) =>
    configuration
      ? state.systems.customSystemsRevisionByConfig[configuration.uniqueId] ?? 0
      : 0
  );
  const equipmentPersistVersion = useAppSelector(
    (state) => state.structure.equipmentPersistVersion
  );
  const requestBodyHash = useAppSelector(
    (state) => state.ptacSelection.systems.requestBodyHash
  );

  const customSelections = useMemo(
    () =>
      mergePtacCustomSelections(
        sessionCustomSelections,
        ptacCustomSelectionsFromCompared(systemsToCompare)
      ),
    [sessionCustomSelections, systemsToCompare]
  );

  useEffect(() => {
    if (
      !isPtac ||
      !configuration ||
      !anchorSystemUniqueId ||
      !isPtacDesignReady(configuration)
    ) {
      return;
    }
    dispatch(
      fetchPtacSystems({
        anchorSystemUniqueId,
        configuration,
        inStockOnly: galleryFilters.inStock,
        priceMin: galleryFilters.priceMin,
        priceMax: galleryFilters.priceMax,
        filters,
        electricalDefaults,
        zipCode,
        customSelections,
        customSystemsRevision
      })
    );
  }, [
    isPtac,
    configuration,
    anchorSystemUniqueId,
    galleryFilters,
    filters,
    electricalDefaults,
    zipCode,
    customSelections,
    customSystemsRevision,
    equipmentPersistVersion,
    requestBodyHash,
    dispatch
  ]);
}
